import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Printer, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { invoiceService } from '../services/invoiceService';
import { useCompanyInfo } from '../hooks/useCompanyInfo';

export default function InvoiceDetail() {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const companyInfo = useCompanyInfo();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  // Deep link from the invoices list: /invoices/:id?print=1 opens the print dialog
  useEffect(() => {
    if (!loading && invoice && searchParams.get('print')) {
      const timer = setTimeout(() => window.print(), 400);
      return () => clearTimeout(timer);
    }
  }, [loading, invoice, searchParams]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const data = await invoiceService.getById(invoiceId);
      setInvoice(data);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load invoice"
      });
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await invoiceService.delete(invoiceId);
      setShowDeleteDialog(false);
      toast({
        title: "Success",
        description: "Invoice deleted successfully"
      });
      navigate('/invoices');
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete invoice"
      });
      setShowDeleteDialog(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await invoiceService.update(invoiceId, { status: newStatus });
      setInvoice(prev => ({ ...prev, status: newStatus }));
      toast({
        title: "Success",
        description: `Invoice marked as ${newStatus}`
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update status"
      });
    }
  };

  const handleSendInvoice = async () => {
    try {
      setSending(true);
      const result = await invoiceService.send(invoiceId);
      
      // Update status if it changed
      if (invoice.status === 'draft') {
        setInvoice(prev => ({ ...prev, status: 'sent' }));
      }
      
      setShowSendDialog(false);
      toast({
        title: "Success",
        description: result.message || 'Invoice sent successfully!'
      });
      
      // Refresh invoice to get updated status from backend
      fetchInvoice();
    } catch (error) {
      console.error('Error sending invoice:', error);
      setShowSendDialog(false);
      if (error.detail && error.detail.includes('not configured')) {
        toast({
          variant: "destructive",
          title: "Email Not Configured",
          description: "Please configure SMTP settings in Settings to send invoices."
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: 'Failed to send invoice: ' + (error.detail || error.message || 'Unknown error')
        });
      }
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800 border-gray-300',
      sent: 'bg-blue-100 text-blue-800 border-blue-300',
      paid: 'bg-green-100 text-green-800 border-green-300',
      overdue: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-6">
        <p>Invoice not found</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/invoices')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Invoices
        </Button>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{invoice.invoice_number}</h1>
            <p className="text-gray-600 mt-1">Invoice Details</p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" />
              Print / PDF
            </Button>
            <Button
              onClick={() => setShowSendDialog(true)}
              disabled={sending}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {sending ? (
                <>Sending...</>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invoice
                </>
              )}
            </Button>
            <Link to={`/invoices/${invoice.id}/edit`}>
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Print styles: only the invoice itself prints, with its colors kept */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #invoice-print-area, #invoice-print-area * {
            visibility: visible;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          #invoice-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            box-shadow: none;
            border-radius: 0;
          }
        }
      `}</style>

      {/* Invoice Content */}
      <div id="invoice-print-area" className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header Section */}
        <div className="text-white p-8" style={{ backgroundColor: '#4478bb' }}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-3xl font-bold">{companyInfo.name}</h2>
              <p className="mt-2">{companyInfo.address1}</p>
              <p>{companyInfo.address2}</p>
              <p className="mt-2">{companyInfo.phone}</p>
              <p>{companyInfo.email}</p>
              <p className="mt-2">CSLB License: {companyInfo.license}</p>
            </div>
            <div className="text-right">
              <h3 className="text-2xl font-bold">INVOICE</h3>
              <p className="text-xl mt-2">{invoice.invoice_number}</p>
              <div className={`mt-4 px-3 py-1 rounded-full inline-block border ${getStatusColor(invoice.status)}`}>
                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Info & Bill To */}
        <div className="p-8 grid grid-cols-2 gap-8 border-b">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">BILL TO:</h3>
            <p className="font-medium">{invoice.customer_first_name} {invoice.customer_last_name}</p>
            <p className="text-gray-600">{invoice.customer_address}</p>
            <p className="text-gray-600">{invoice.customer_phone}</p>
            <p className="text-gray-600">{invoice.customer_email}</p>
          </div>
          <div className="text-right">
            <div className="mb-4">
              <p className="text-gray-600">Invoice Date</p>
              <p className="font-medium">{formatDate(invoice.invoice_date)}</p>
            </div>
            <div className="mb-4">
              <p className="text-gray-600">Due Date</p>
              <p className="font-medium">{formatDate(invoice.due_date)}</p>
            </div>
            <div>
              <p className="text-gray-600">Payment Terms</p>
              <p className="font-medium">{invoice.payment_terms}</p>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="p-8">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-3 font-semibold">DESCRIPTION</th>
                <th className="text-center py-3 font-semibold w-24">QTY</th>
                <th className="text-right py-3 font-semibold w-32">PRICE</th>
                <th className="text-right py-3 font-semibold w-32">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {invoice.line_items && invoice.line_items.map((item, index) => (
                <tr key={index} className="border-b border-gray-200">
                  <td className="py-4">{item.description}</td>
                  <td className="py-4 text-center">{item.quantity}</td>
                  <td className="py-4 text-right">{formatCurrency(item.price)}</td>
                  <td className="py-4 text-right font-medium">{formatCurrency(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="p-8 bg-gray-50 border-t">
          <div className="max-w-md ml-auto space-y-2">
            <div className="flex justify-between text-gray-700">
              <span>Subtotal:</span>
              <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.tax_rate > 0 && (
              <div className="flex justify-between text-gray-700">
                <span>Tax ({invoice.tax_rate}%):</span>
                <span className="font-medium">{formatCurrency(invoice.tax_amount)}</span>
              </div>
            )}
            {invoice.discount > 0 && (
              <div className="flex justify-between text-gray-700">
                <span>Discount:</span>
                <span className="font-medium">-{formatCurrency(invoice.discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t-2 border-gray-300">
              <span>TOTAL:</span>
              <span className="text-brand-600">{formatCurrency(invoice.total)}</span>
            </div>
            {invoice.status === 'paid' && (
              <div className="text-center mt-4 p-2 bg-green-100 text-green-800 rounded">
                ✓ PAID
              </div>
            )}
          </div>
        </div>

        {/* Payment Info & Terms */}
        <div className="p-8 grid grid-cols-2 gap-8 border-t">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">PAYMENT INFORMATION</h3>
            <p className="text-sm text-gray-600">Make checks payable to:</p>
            <p className="text-sm font-medium">{companyInfo.name}</p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">TERMS & CONDITIONS</h3>
            <p className="text-sm text-gray-600">{invoice.terms_conditions}</p>
          </div>
        </div>

        {invoice.notes && (
          <div className="p-8 bg-yellow-50 border-t">
            <h3 className="font-semibold text-gray-900 mb-2">INTERNAL NOTES</h3>
            <p className="text-sm text-gray-700">{invoice.notes}</p>
          </div>
        )}
      </div>

      {/* Status Actions */}
      {invoice.status !== 'paid' && (
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Update Status</h3>
          <div className="flex gap-2">
            {invoice.status === 'draft' && (
              <Button
                onClick={() => handleStatusChange('sent')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Mark as Sent
              </Button>
            )}
            {(invoice.status === 'sent' || invoice.status === 'overdue') && (
              <Button
                onClick={() => handleStatusChange('paid')}
                className="bg-green-600 hover:bg-green-700"
              >
                Mark as Paid
              </Button>
            )}
            {invoice.status === 'sent' && (
              <Button
                onClick={() => handleStatusChange('overdue')}
                variant="outline"
                className="text-red-600 hover:text-red-700"
              >
                Mark as Overdue
              </Button>
            )}
            <Button
              onClick={() => handleStatusChange('draft')}
              variant="outline"
            >
              Mark as Draft
            </Button>
          </div>
        </div>
      )}

      {/* Send Confirmation Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Invoice</DialogTitle>
            <DialogDescription>
              Send this invoice to {invoice?.customer_email}?
              <br />
              The invoice will be emailed to the customer and status will be updated to "Sent".
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendInvoice}
              disabled={sending}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {sending ? 'Sending...' : 'Send Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Invoice</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this invoice? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
